import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPrograms,
  fetchInquiryByPatient,
  fetchPatientDataForInquiry,
  updateInquiry,
  createInquiry,
  clearError,
} from "../../../store/slices/inquirySlice";
import Swal from "sweetalert2";
import EnquirerInfo from "./EnquiryForm/EnquirerInfo";
import ClientInfo from "./EnquiryForm/ClientInfo";
import Medications from "./EnquiryForm/Medications";
import PhysicalHealth from "./EnquiryForm/PhysicalHealth";
import RiskAssessment from "./EnquiryForm/RiskAssessment";
import AggressionHistory from "./EnquiryForm/AggressionHistory";
import ClientBehavior from "./EnquiryForm/ClientBehavior";
import TreatmentPlan from "./EnquiryForm/TreatmentPlan";


const AddPatientInquiry = () => {
  const { id: patientId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading, error } = useSelector((state) => state.inquiries);
  const { programs, isProgramsLoading } = useSelector((state) => state.inquiries);
  const location = useLocation();
  const isAddMode = location.pathname.includes("add-inquiry");

  const [activeTab, setActiveTab] = useState("enquirer-info");
  
  // Initialize program from URL if present (from OPD Referral)
  const queryParams = new URLSearchParams(location.search);
  const preFilledProgramId = queryParams.get("program_id");
  
  const [selectedProgram, setSelectedProgram] = useState(preFilledProgramId || "2"); // Default to 2 if not provided
  const [isEditing, setIsEditing] = useState(false);
  const [inquiryLoaded, setInquiryLoaded] = useState(false);
  const { currentPatient } = useSelector((state) => state.inquiries);

  const [formData, setFormData] = useState({
    contactMethod: "",
    inquirerName: "",
    relationWithClient: "",
    phone1: "",
    phone2: "",
    email: "",
    address: "",
    howHeard: [],
    referredBy: { psychiatrist: "", psychologist: "", otherMedical: "" },
    otherSource: "",
    clientName: "",
    clientGender: "",
    clientEducation: "",
    clientBirthOrder: "",
    clientSiblings: "",
    clientDOB: "",
    client_cnic: "",
    client_phone: "",
    client_email: "",
    client_address: "",
    familyMentalIllness: "",
    familyMentalIllnessDetails: "",
    diagnosis: "",
    diagnosisSince: "",
    currentCare: "",
    trhServices: "",
    trhServicesDetails: "",
    substanceUse: "",
    substanceUseDetails: "",
    medications: [{ name: "", dosage: "" }],
    prescribedBy: "",
    prescriptionDate: "",
    prevServices: [],
    otherService: "",
    prevHospitalizations: [{ hospital: "", duration: "", consultant: "" }],
    medicalConditions: "",
    medicalConditionsDetails: "",
    healthIssues: [],
    otherHealthIssue: "",
    illnessHistory: "",
    expectations: "",
    presentingComplaints: "",
    // Section A: Suicide Risk
    suicidal_thoughts: "",
    frequency_suicidal_thoughts: "",
    current_or_past_suicide_plans: "",
    previous_suicide_attempts: "",
    previous_suicide_attempts_details: "",
    // Section B: Escape Risk
    desire_to_escape: "",
    past_escape_attempts: "",
    past_escape_attempts_details: "",
    // Section C: Aggression / Violence Risk
    history_of_aggression: "",
    history_of_aggression_details: "",
    current_risk_of_harm_to_others: "",
    specific_individuals: "",
    // Section D: Self-Harm Risk
    history_of_self_harm: "",
    history_of_self_harm_details: "",
    current_self_harm_urges: "",
    specific_self_harm_actions: "",
    recommendedFor: [],
    opdDetails: "",
    followUpNotes: "",
    // Aggression History (Program 2)
    prev_aggression: "",
    last_episode: "",
    incident_provoked: "",
    regret_incident: "",
    show_denial: "",
    episode_description: "",
    cope_stress: "",
    // Client Behavior (Program 2)
    client_general_behavior: "",
    impulsive_behavior: "",
    impulsive_example_1: "",
    impulsive_example_2: "",
    impulsive_example_3: "",
    symptoms: "",
    rehab_expectations: "",
    fear_aggression: "",
    psychiatrist_note: "",
    treatmentOption: "",
    reason: "",
    amount: "",
    payment_status: "",
    reference_no: "",
    payment_date: "",
  });

  const [errors, setErrors] = useState({});

  // Map backend inquiry -> local formData
  const mapInquiryToFormData = (inquiry) => {
    if (!inquiry) return {};
    const master = inquiry.master || {};
    const clientInfo = inquiry.clientInformation || {};
    const clientMed = inquiry.clientMedications || {};
    const medsList = inquiry.medicationsList || [];
    const prevHosp = inquiry.prevHospitalizations || [];
    const history = inquiry.clientHistory || {};
    const risk = inquiry.riskForm || {};
    const hear = inquiry.hearAboutUs || [];
    const aggressionHistory = inquiry.aggressionHistory || {};
    const clientBehavior = inquiry.clientBehavior || {};
    const treatmentPlan = inquiry.treatmentPlan || {};

    const medications = medsList.length
      ? medsList.map((m) => ({ name: m.name || "", dosage: m.dosage || "" }))
      : [{ name: "", dosage: "" }];

    const howHeard = hear.map((h) => h.value);
    const referredBy = {
      psychiatrist:
        hear.find((h) => h.value === "Referred by: Psychiatrist")?.name || "",
      psychologist:
        hear.find((h) => h.value === "Referred by: Psychologist")?.name || "",
      otherMedical:
        hear.find((h) => h.value === "Referred by: Medical Practitioner")
          ?.name || "",
    };

    return {
      inquiry_id: master.id || null,
      contactMethod: master.contact_method || "",
      inquirerName: master.inquirer_name || "",
      relationWithClient: master.relation_with_client || "",
      phone1: master.phone1 || "",
      phone2: master.phone2 || "",
      email: master.email || "",
      address: master.address || "",
      amount: master.amount || "",
      payment_status: master.payment_status || "",
      reference_no: master.reference_no || "",
      payment_date: master.payment_date || "",
      howHeard,
      referredBy,
      otherSource:
        hear.find((h) => h.value === "Other")?.name ||
        master.other_source ||
        "",
      clientName: clientInfo.client_name || "",
      clientGender: clientInfo.client_gender || "",
      clientEducation: clientInfo.client_education || "",
      clientBirthOrder: clientInfo.client_birth_order || "",
      clientSiblings: clientInfo.client_siblings || "",
      clientDOB: clientInfo.client_dob || "",
      client_cnic: clientInfo.client_cnic || "",
      client_phone: clientInfo.client_phone || "",
      client_email: clientInfo.client_email || "",
      client_address: clientInfo.client_address || "",
      familyMentalIllness: clientInfo.family_mental_illness || "",
      familyMentalIllnessDetails: clientInfo.family_mental_illness_details || "",
      diagnosis: clientInfo.diagnosis || "",
      diagnosisSince: clientInfo.diagnosis_since || "",
      currentCare: clientInfo.current_care || "",
      trhServices: clientInfo.trh_services || "",
      trhServicesDetails: clientInfo.trh_services_details || "",
      medications,
      prescribedBy: clientMed.prescribed_by || "",
      prescriptionDate: clientMed.prescription_date || "",
      prevServices: Array.isArray(clientMed.prev_services)
        ? clientMed.prev_services
        : JSON.parse(clientMed.prev_services || "[]"),
      otherService: clientMed.other_service || "",
      prevHospitalizations: prevHosp.length
        ? prevHosp.map((h) => ({
          hospital: h.hospital || "",
          duration: h.duration || "",
          consultant: h.consultant || "",
        }))
        : [{ hospital: "", duration: "", consultant: "" }],
      medicalConditions: history.medical_conditions || "",
      medicalConditionsDetails: history.medical_conditions_details || "",
      healthIssues: Array.isArray(history.experienced_issues)
        ? history.experienced_issues
        : history.experienced_issues?.split(",") || [],
      otherHealthIssue: history.other_health_issue || "",
      illnessHistory: history.illness_history || "",
      expectations: history.expectations || "",
      suicidal_thoughts: risk.suicidal_thoughts || "",
      frequency_suicidal_thoughts: risk.frequency_suicidal_thoughts || "",
      current_or_past_suicide_plans: risk.current_or_past_suicide_plans || "",
      previous_suicide_attempts: risk.previous_suicide_attempts || "",
      previous_suicide_attempts_details: risk.previous_suicide_attempts_details || "",
      desire_to_escape: risk.desire_to_escape || "",
      past_escape_attempts: risk.past_escape_attempts || "",
      past_escape_attempts_details: risk.past_escape_attempts_details || "",
      history_of_aggression: risk.history_of_aggression || "",
      history_of_aggression_details: risk.history_of_aggression_details || "",
      current_risk_of_harm_to_others: risk.current_risk_of_harm_to_others || "",
      specific_individuals: risk.specific_individuals || "",
      history_of_self_harm: risk.history_of_self_harm || "",
      history_of_self_harm_details: risk.history_of_self_harm_details || "",
      current_self_harm_urges: risk.current_self_harm_urges || "",
      specific_self_harm_actions: risk.specific_self_harm_actions || "",
      recommendedFor: Array.isArray(risk.recommended_for)
        ? risk.recommended_for
        : [risk.recommended_for || ""].filter(Boolean),
      opdDetails: risk.opd_details || "",
      followUpNotes: risk.follow_up_notes || "",
      prev_aggression: aggressionHistory.prev_aggression || "",
      last_episode: aggressionHistory.last_episode || "",
      incident_provoked: aggressionHistory.incident_provoked || "",
      regret_incident: aggressionHistory.regret_incident || "",
      show_denial: aggressionHistory.show_denial || "",
      episode_description: aggressionHistory.episode_description || "",
      cope_stress: aggressionHistory.cope_stress || "",
      client_general_behavior: clientBehavior.client_general_behavior || "",
      impulsive_behavior: clientBehavior.impulsive_behavior || "",
      impulsive_example_1: clientBehavior.impulsive_example_1 || "",
      impulsive_example_2: clientBehavior.impulsive_example_2 || "",
      impulsive_example_3: clientBehavior.impulsive_example_3 || "",
      symptoms: clientBehavior.symptoms || "",
      rehab_expectations: clientBehavior.rehab_expectations || "",
      fear_aggression: clientBehavior.fear_aggression || "",
      psychiatrist_note: clientBehavior.psychiatrist_note || "",
      treatmentOption: treatmentPlan.treatment_option || "",
      reason: treatmentPlan.reason || "",
    };
  };

  useEffect(() => {
    dispatch(clearError());
    return () => dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    if (!inquiryLoaded) {
      dispatch(fetchPatientDataForInquiry(patientId));
    }
  }, [dispatch, patientId, inquiryLoaded]);

  useEffect(() => {
    if (currentPatient && !isEditing) {
      setFormData((prev) => ({
        ...prev,
        clientName: `${currentPatient.first_name || ""} ${currentPatient.last_name || ""}`.trim(),
        clientGender: currentPatient.gender || "",
        clientDOB: currentPatient.dob || "",
        client_cnic: currentPatient.cnic || "",
        client_phone: currentPatient.phone || "",
        client_address: currentPatient.address || "",
        client_email: currentPatient.email || "",
      }));
    }
  }, [currentPatient, isEditing]);

  useEffect(() => {
    dispatch(fetchPrograms());
  }, [dispatch]);

  useEffect(() => {
    if (isAddMode) {
      setIsEditing(false);
      setInquiryLoaded(true);
      return;
    }

    const loadInquiry = async () => {
      try {
        const inquiryData = await dispatch(fetchInquiryByPatient(patientId)).unwrap();
        if (inquiryData) {
          const mapped = mapInquiryToFormData(inquiryData);
          setFormData((prev) => ({ ...prev, ...mapped }));
          setIsEditing(true);
        } else {
          setIsEditing(false);
        }
      } catch {
        setIsEditing(false);
      } finally {
        setInquiryLoaded(true);
      }
    };

    if (!inquiryLoaded) loadInquiry();
  }, [dispatch, patientId, inquiryLoaded, isAddMode]);

  // ================= VALIDATION FUNCTIONS =================

  const validateEnquirerInfo = () => {
    const newErrors = {};

    if (!formData.contactMethod) {
      newErrors.contactMethod = "Contact method is required";
    }

    if (!formData.inquirerName?.trim()) {
      newErrors.inquirerName = "Inquirer name is required";
    }

    if (!formData.phone1?.trim()) {
      newErrors.phone1 = "Phone number is required";
    }

    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    }

    if (!formData.relationWithClient) {
      newErrors.relationWithClient = "Relation with client is required";
    }

    return newErrors;
  };

  const validateClientInfo = () => {
    const newErrors = {};

    if (!formData.clientName?.trim()) {
      newErrors.clientName = "Client name is required";
    }

    if (!formData.clientGender?.trim()) {
      newErrors.clientGender = "Gender is required";
    }

    if (!formData.clientDOB?.trim()) {
      newErrors.clientDOB = "Date of birth is required";
    }

    if (!formData.client_phone?.trim()) {
      newErrors.client_phone = "Phone number is required";
    }

    if (!formData.client_email?.trim()) {
      newErrors.client_email = "Email is required";
    }

    if (!formData.client_address?.trim()) {
      newErrors.client_address = "Address is required";
    }

    return newErrors;
  };

  const validateMedications = () => {
    const newErrors = {};

    if (!formData.medications?.[0]?.name?.trim()) {
      newErrors.medications = "At least one medication is required";
    }

    if (!formData.prescribedBy?.trim()) {
      newErrors.prescribedBy = "Prescribed by is required";
    }

    if (!formData.prescriptionDate) {
      newErrors.prescriptionDate = "Prescription date is required";
    }

    return newErrors;
  };

  const validatePhysicalHealth = () => {
    const newErrors = {};

    if (!formData.medicalConditions) {
      newErrors.medicalConditions = "Please specify if there are known medical conditions";
    }

    if (formData.medicalConditions === "Yes" && !formData.medicalConditionsDetails?.trim()) {
      newErrors.medicalConditionsDetails = "Please specify the medical conditions";
    }

    if (!formData.illnessHistory?.trim()) {
      newErrors.illnessHistory = "Brief history of illness is required";
    }

    if (!formData.expectations?.trim()) {
      newErrors.expectations = "Expectations from rehabilitation services is required";
    }

    return newErrors;
  };

  const validateRiskAssessment = () => {
    const newErrors = {};

    // NOTE: the shared RiskAssessment component binds camelCase field names
    // (suicidalThoughts, etc.) and the submit mapping reads those first, so the
    // validation must check the SAME camelCase keys — otherwise it checks fields
    // the UI never fills and the error highlighting never lines up.

    // Section A: Suicide Risk
    if (!formData.suicidalThoughts) {
      newErrors.suicidalThoughts = "Please specify if there are suicidal thoughts";
    }

    if (formData.suicidalThoughts === "Yes" && !formData.suicideFrequency) {
      newErrors.suicideFrequency = "Please specify frequency of suicidal thoughts";
    }

    if (!formData.suicidePlans) {
      newErrors.suicidePlans = "Please specify if there are current or past suicide plans";
    }

    if (!formData.suicideAttempts) {
      newErrors.suicideAttempts = "Please specify if there were previous suicide attempts";
    }

    if (formData.suicideAttempts === "Yes" && !formData.suicideAttemptsDetails?.trim()) {
      newErrors.suicideAttemptsDetails = "Please provide details of previous suicide attempts";
    }

    // Section B: Escape Risk
    if (!formData.desireEscape) {
      newErrors.desireEscape = "Please specify if there is a desire to escape";
    }

    if (!formData.escapeAttempts) {
      newErrors.escapeAttempts = "Please specify if there were past escape attempts";
    }

    if (formData.escapeAttempts === "Yes" && !formData.escapeAttemptsDetails?.trim()) {
      newErrors.escapeAttemptsDetails = "Please provide details of past escape attempts";
    }

    // Section C: Aggression Risk
    if (!formData.historyAggression) {
      newErrors.historyAggression = "Please specify if there is a history of aggression";
    }

    if (formData.historyAggression === "Yes" && !formData.historyAggressionDetails?.trim()) {
      newErrors.historyAggressionDetails = "Please provide details of history of aggression";
    }

    if (!formData.harmToOthers) {
      newErrors.harmToOthers = "Please specify if there is a current risk of harm to others";
    }

    if (formData.harmToOthers === "Yes" && !formData.harmToOthersDetails?.trim()) {
      newErrors.harmToOthersDetails = "Please specify any specific individuals at risk";
    }

    // Section D: Self-Harm Risk
    if (!formData.selfHarmHistory) {
      newErrors.selfHarmHistory = "Please specify if there is a history of self-harm";
    }

    if (formData.selfHarmHistory === "Yes" && !formData.selfHarmHistoryDetails?.trim()) {
      newErrors.selfHarmHistoryDetails = "Please provide details of history of self-harm";
    }

    if (!formData.selfHarmUrges) {
      newErrors.selfHarmUrges = "Please specify if there are current self-harm urges";
    }

    if (formData.selfHarmUrges === "Yes" && !formData.selfHarmUrgesDetails?.trim()) {
      newErrors.selfHarmUrgesDetails = "Please specify specific actions considered";
    }

    // Recommended For
    if (formData.recommendedFor.length === 0) {
      newErrors.recommendedFor = "Please select at least one recommendation";
    }

    if (formData.recommendedFor.includes("OPD Services outside TRH") && !formData.opdDetails?.trim()) {
      newErrors.opdDetails = "Please provide details for OPD services";
    }

    return newErrors;
  };

  const validateAggressionHistory = () => {
    const newErrors = {};

    if (!formData.prev_aggression) {
      newErrors.prev_aggression = "Please specify if there was a previous episode of aggression/violence";
    }

    if (formData.prev_aggression === "Yes") {
      if (!formData.last_episode?.trim()) {
        newErrors.last_episode = "Please specify when was the last episode";
      }

      if (!formData.incident_provoked) {
        newErrors.incident_provoked = "Please specify if the incident was provoked";
      }

      if (!formData.regret_incident) {
        newErrors.regret_incident = "Please specify if he/she regrets the incident";
      }

      if (!formData.show_denial) {
        newErrors.show_denial = "Please specify if he/she shows denial";
      }

      if (!formData.episode_description?.trim()) {
        newErrors.episode_description = "Please describe the episode briefly";
      }
    }

    if (!formData.cope_stress?.trim()) {
      newErrors.cope_stress = "Please describe how the client handles/copes with stress";
    }

    return newErrors;
  };

  const validateClientBehavior = () => {
    const newErrors = {};

    if (!formData.client_general_behavior) {
      newErrors.client_general_behavior = "Please select the client's general behavior";
    }

    if (!formData.impulsive_behavior) {
      newErrors.impulsive_behavior = "Please specify if the client exhibits impulsive behavior";
    }

    if (formData.impulsive_behavior === "Yes") {
      if (!formData.impulsive_example_1?.trim()) {
        newErrors.impulsive_example_1 = "Please provide first example of impulsive behavior";
      }
      if (!formData.impulsive_example_2?.trim()) {
        newErrors.impulsive_example_2 = "Please provide second example of impulsive behavior";
      }
      if (!formData.impulsive_example_3?.trim()) {
        newErrors.impulsive_example_3 = "Please provide third example of impulsive behavior";
      }
    }

    if (!formData.symptoms?.trim()) {
      newErrors.symptoms = "Please describe the symptoms";
    }

    if (!formData.rehab_expectations?.trim()) {
      newErrors.rehab_expectations = "Please describe expectations from rehabilitation services";
    }

    if (!formData.fear_aggression) {
      newErrors.fear_aggression = "Please specify if you fear another episode of violence/aggression";
    }

    return newErrors;
  };

  const validateTreatmentPlan = () => {
    const newErrors = {};

    if (!formData.treatmentOption) {
      newErrors.treatmentOption = "Please select a treatment plan";
    }

    if (!formData.reason?.trim()) {
      newErrors.reason = "Please provide a reason for the selected treatment plan";
    }

    return newErrors;
  };

  // ================= NEXT BUTTON HANDLERS =================

  const handleEnquirerNext = () => {
    const errs = validateEnquirerInfo();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setActiveTab("client-info");
  };

  const handleClientNext = () => {
    const errs = validateClientInfo();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setActiveTab("medications");
  };

  const handleMedicationsNext = () => {
    const errs = validateMedications();
    setErrors(errs);
    if (Object.keys(errs).length) return;

    if (selectedProgram === "2") {
      setActiveTab("aggression-history");
    } else {
      setActiveTab("physical-health");
    }
  };

  const handlePhysicalHealthNext = () => {
    const errs = validatePhysicalHealth();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setActiveTab("risk-assessment");
  };

  const handleAggressionHistoryNext = () => {
    const errs = validateAggressionHistory();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setActiveTab("client-behavior");
  };

  const handleClientBehaviorNext = () => {
    const errs = validateClientBehavior();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setActiveTab("treatment-plan");
  };

  // ================= FORM HANDLERS =================

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // ---------- CHECKBOX ----------
    if (type === "checkbox") {
      const updatedArray = checked
        ? [...formData[name], value]
        : formData[name].filter((item) => item !== value);

      setFormData({ ...formData, [name]: updatedArray });
      return;
    }

    // ---------- REFERRED BY ----------
    if (name.includes("referredBy")) {
      const [, subKey] = name.split(".");
      setFormData({
        ...formData,
        referredBy: { ...formData.referredBy, [subKey]: value },
      });
      return;
    }

    // ---------- ARRAY FIELDS ----------
    if (name.includes("medications") || name.includes("prevHospitalizations")) {
      const [field, index, subField] = name.split(".");
      const updatedArray = [...formData[field]];
      updatedArray[index] = { ...updatedArray[index], [subField]: value };
      setFormData({ ...formData, [field]: updatedArray });
      return;
    }

    // ---------- CNIC MASKING ----------
    if (name === "client_cnic") {
      let digits = value.replace(/\D/g, "").slice(0, 13);

      let masked = digits;
      if (digits.length > 5)
        masked = `${digits.slice(0, 5)}-${digits.slice(5)}`;
      if (digits.length > 12)
        masked = `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12)}`;

      setFormData({ ...formData, client_cnic: masked });
      return;
    }

    // ---------- PHONE MASKING ----------
    if (["phone1", "phone2", "client_phone"].includes(name)) {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 11);
      setFormData({ ...formData, [name]: digitsOnly });
      return;
    }

    // ---------- DEFAULT ----------
    setFormData({ ...formData, [name]: value });
  };

  const addMedication = () =>
    setFormData({
      ...formData,
      medications: [...formData.medications, { name: "", dosage: "" }],
    });

  const removeMedication = (index) =>
    setFormData({
      ...formData,
      medications: formData.medications.filter((_, i) => i !== index),
    });

  const addHospitalization = () => {
    setFormData({
      ...formData,
      prevHospitalizations: [
        ...formData.prevHospitalizations,
        { hospital: "", duration: "", consultant: "" },
      ],
    });
  };

  const removeHospitalization = (index) => {
    const updatedHosp = formData.prevHospitalizations.filter((_, i) => i !== index);
    setFormData({ ...formData, prevHospitalizations: updatedHosp });
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate EVERY step (tabs are directly clickable, so earlier steps may
    // have been skipped). Aggregate missing fields grouped by their tab so the
    // popup can tell the user exactly what to fix and where.
    const groups = [
      { tab: "enquirer-info", name: "Enquirer Information", errs: validateEnquirerInfo() },
      { tab: "client-info", name: "Client Information", errs: validateClientInfo() },
      { tab: "medications", name: "Medications", errs: validateMedications() },
    ];
    if (selectedProgram === "2") {
      groups.push({ tab: "aggression-history", name: "Aggression History", errs: validateAggressionHistory() });
      groups.push({ tab: "client-behavior", name: "Client Behavior", errs: validateClientBehavior() });
      groups.push({ tab: "treatment-plan", name: "Treatment Plan", errs: validateTreatmentPlan() });
    } else {
      groups.push({ tab: "physical-health", name: "Physical Health", errs: validatePhysicalHealth() });
      groups.push({ tab: "risk-assessment", name: "Risk Assessment", errs: validateRiskAssessment() });
    }

    const merged = {};
    groups.forEach((g) => Object.assign(merged, g.errs));

    if (Object.keys(merged).length > 0) {
      // Highlight every missing field (across all tabs) in red.
      setErrors(merged);

      // Jump to the first tab that has a missing field.
      const firstBadTab = groups.find((g) => Object.keys(g.errs).length > 0);
      if (firstBadTab) setActiveTab(firstBadTab.tab);

      // Build a grouped list: "Tab name" -> the missing fields under it.
      const listHtml = groups
        .filter((g) => Object.keys(g.errs).length > 0)
        .map((g) => {
          const items = Object.values(g.errs).map((m) => `<li>${m}</li>`).join("");
          return `<div class="mb-2"><div class="fw-bold text-primary">${g.name}</div><ul class="mb-0 ps-3">${items}</ul></div>`;
        })
        .join("");

      Swal.fire({
        title: "Incomplete Form",
        html: `<div class="text-start"><p class="mb-2">Please fill in the following required fields:</p>${listHtml}</div>`,
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    try {
      // Convert frontend RiskAssessment fields (camelCase) → backend snake_case
      const riskSection = {
        suicidal_thoughts: formData.suicidalThoughts || formData.suicidal_thoughts,
        frequency_suicidal_thoughts: formData.suicideFrequency || formData.frequency_suicidal_thoughts,
        current_or_past_suicide_plans: formData.suicidePlans || formData.current_or_past_suicide_plans,
        previous_suicide_attempts: formData.suicideAttempts || formData.previous_suicide_attempts,
        previous_suicide_attempts_details: formData.suicideAttemptsDetails || formData.previous_suicide_attempts_details,

        desire_to_escape: formData.desireEscape || formData.desire_to_escape,
        past_escape_attempts: formData.escapeAttempts || formData.past_escape_attempts,
        past_escape_attempts_details: formData.escapeAttemptsDetails || formData.past_escape_attempts_details,

        history_of_aggression: formData.historyAggression || formData.history_of_aggression,
        history_of_aggression_details: formData.historyAggressionDetails || formData.history_of_aggression_details,
        current_risk_of_harm_to_others: formData.harmToOthers || formData.current_risk_of_harm_to_others,
        specific_individuals: formData.harmToOthersDetails || formData.specific_individuals,

        history_of_self_harm: formData.selfHarmHistory || formData.history_of_self_harm,
        history_of_self_harm_details: formData.selfHarmHistoryDetails || formData.history_of_self_harm_details,
        current_self_harm_urges: formData.selfHarmUrges || formData.current_self_harm_urges,
        specific_self_harm_actions: formData.selfHarmUrgesDetails || formData.specific_self_harm_actions,
        treatment_option: formData.treatmentOption,
      };

      const payload = {
        ...formData,
        ...riskSection,
        patient_id: patientId,
        program_master_id: selectedProgram,
      };

      if (isEditing && formData.inquiry_id) {
        await dispatch(
          updateInquiry({ id: formData.inquiry_id, inquiryData: payload })
        ).unwrap();
        Swal.fire({
          title: "Updated!",
          text: "Inquiry updated successfully.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
          position: "top",
        });
      } else {
        await dispatch(createInquiry({ patientId, inquiryData: payload })).unwrap();
        Swal.fire({
          title: "Submitted!",
          text: "Inquiry submitted successfully.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
          position: "top",
        });
      }

      navigate(`/patients/manage/${patientId}/cases`);
    } catch (error) {
      const errorMessage = error.errors
        ? Object.values(error.errors).flat().join(", ")
        : error.message || JSON.stringify(error);
      Swal.fire({
        title: "Error!",
        text: `Failed to submit inquiry: ${errorMessage}`,
        icon: "error",
        confirmButtonColor: "#d33",
        width: "320px",
        position: "top",
      });
    }
  };

  // Tabs based on program
  const getTabs = () => {
    if (selectedProgram === "1" || selectedProgram === "3") {
      return [
        { key: "enquirer-info", label: "Enquirer Info", icon: "ri-contacts-line" },
        { key: "client-info", label: "Client Info", icon: "ri-user-line" },
        { key: "medications", label: "Medications", icon: "ri-medicine-bottle-line" },
        { key: "physical-health", label: "Physical Health", icon: "ri-heart-line" },
        { key: "risk-assessment", label: "Risk Assessment", icon: "ri-shield-line" },
      ];
    }
    return [
      { key: "enquirer-info", label: "Enquirer Info", icon: "ri-contacts-line" },
      { key: "client-info", label: "Client Info", icon: "ri-user-line" },
      { key: "medications", label: "Medications", icon: "ri-medicine-bottle-line" },
      { key: "aggression-history", label: "Aggression History", icon: "ri-emotion-line" },
      { key: "client-behavior", label: "Client Behavior", icon: "ri-psychotherapy-line" },
      { key: "treatment-plan", label: "Treatment Plan", icon: "ri-file-list-3-line" },
    ];
  };

  const tabs = getTabs();

  // Reset all form data to initial empty values
  const resetFormData = () => ({
    contactMethod: "",
    inquirerName: "",
    relationWithClient: "",
    phone1: "",
    phone2: "",
    email: "",
    address: "",
    howHeard: [],
    referredBy: { psychiatrist: "", psychologist: "", otherMedical: "" },
    otherSource: "",
    clientName: formData.clientName,
    clientGender: formData.clientGender,
    clientEducation: "",
    clientBirthOrder: "",
    clientSiblings: "",
    clientDOB: formData.clientDOB,
    client_cnic: formData.client_cnic,
    client_phone: formData.client_phone,
    client_email: formData.client_email,
    client_address: formData.client_address,
    familyMentalIllness: "",
    familyMentalIllnessDetails: "",
    diagnosis: "",
    diagnosisSince: "",
    currentCare: "",
    trhServices: "",
    trhServicesDetails: "",
    substanceUse: "",
    substanceUseDetails: "",
    medications: [{ name: "", dosage: "" }],
    prescribedBy: "",
    prescriptionDate: "",
    prevServices: [],
    otherService: "",
    prevHospitalizations: [{ hospital: "", duration: "", consultant: "" }],
    medicalConditions: "",
    medicalConditionsDetails: "",
    healthIssues: [],
    otherHealthIssue: "",
    illnessHistory: "",
    expectations: "",
    presentingComplaints: "",
    suicidal_thoughts: "",
    frequency_suicidal_thoughts: "",
    current_or_past_suicide_plans: "",
    previous_suicide_attempts: "",
    previous_suicide_attempts_details: "",
    desire_to_escape: "",
    past_escape_attempts: "",
    past_escape_attempts_details: "",
    history_of_aggression: "",
    history_of_aggression_details: "",
    current_risk_of_harm_to_others: "",
    specific_individuals: "",
    history_of_self_harm: "",
    history_of_self_harm_details: "",
    current_self_harm_urges: "",
    specific_self_harm_actions: "",
    recommendedFor: [],
    opdDetails: "",
    followUpNotes: "",
    prev_aggression: "",
    last_episode: "",
    incident_provoked: "",
    regret_incident: "",
    show_denial: "",
    episode_description: "",
    cope_stress: "",
    client_general_behavior: "",
    impulsive_behavior: "",
    impulsive_example_1: "",
    impulsive_example_2: "",
    impulsive_example_3: "",
    symptoms: "",
    rehab_expectations: "",
    fear_aggression: "",
    psychiatrist_note: "",
    treatmentOption: "",
    reason: "",
  });

  return (
    <div className="enquiry-form-wrapper container-xxl">
      <div className="bs-stepper wizard-vertical">
        <div className="bs-stepper-header">
          {tabs.map((tab) => (
            <div className="step" key={tab.key}>
              <button
                type="button"
                className={`step-trigger ${activeTab === tab.key ? "active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                <span className="avatar">
                  <span className="avatar-initial rounded-2 bg-primary text-white">
                    <i className={`${tab.icon} ri-24px`}></i>
                  </span>
                </span>
                <span className="bs-stepper-label">
                  <span className="bs-stepper-title">{tab.label}</span>
                </span>
              </button>
            </div>
          ))}
        </div>

        <div className="bs-stepper-content">
          {/* Program Dropdown */}
          <div className="row mb-4 justify-content-end">
            <div className="col-md-6">
              <div className="form-floating form-floating-outline">
                <select
                  className="form-select"
                  value={selectedProgram}
                  disabled={!!preFilledProgramId}
                  onChange={async (e) => {
                    const newProgram = e.target.value;
                    if (newProgram === selectedProgram) return;

                    const result = await Swal.fire({
                      title: "Change Program?",
                      text: "Changing the program will clear all form data. Do you want to continue?",
                      icon: "warning",
                      showCancelButton: true,
                      confirmButtonColor: "#3085d6",
                      cancelButtonColor: "#d33",
                      confirmButtonText: "Yes, change it",
                      cancelButtonText: "Cancel",
                      width: "340px",
                      position: "top",
                    });

                    if (result.isConfirmed) {
                      setSelectedProgram(newProgram);
                      setFormData(resetFormData());
                      setActiveTab("enquirer-info");

                      Swal.fire({
                        title: "Program Changed!",
                        text: "Please refill the form for the new program.",
                        icon: "info",
                        timer: 1500,
                        showConfirmButton: false,
                        position: "top",
                      });
                    }
                  }}
                >
                  {isProgramsLoading ? (
                    <option>Loading programs...</option>
                  ) : (
                    programs.map((program) => (
                      <option key={program.id} value={program.id}>
                        {program.name}
                      </option>
                    ))
                  )}
                </select>
                <label>Select Program</label>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {activeTab === "enquirer-info" && (
              <EnquirerInfo
                formData={formData}
                handleChange={handleChange}
                setActiveTab={setActiveTab}
                selectedProgram={selectedProgram}
                errors={errors}
                onNext={handleEnquirerNext}
              />
            )}

            {activeTab === "client-info" && (
              <ClientInfo
                formData={formData}
                handleChange={handleChange}
                setActiveTab={setActiveTab}
                selectedProgram={selectedProgram}
                isAdd={true}
                errors={errors}
                onNext={handleClientNext}
              />
            )}

            {activeTab === "medications" && (
              <Medications
                formData={formData}
                handleChange={handleChange}
                addMedication={addMedication}
                removeMedication={removeMedication}
                addHospitalization={addHospitalization}
                removeHospitalization={removeHospitalization}
                setActiveTab={setActiveTab}
                selectedProgram={selectedProgram}
                errors={errors}
                onNext={handleMedicationsNext}
              />
            )}

            {(selectedProgram === "1" || selectedProgram === "3") && activeTab === "physical-health" && (
              <PhysicalHealth
                formData={formData}
                handleChange={handleChange}
                setActiveTab={setActiveTab}
                selectedProgram={selectedProgram}
                errors={errors}
                onNext={handlePhysicalHealthNext}
              />
            )}

            {(selectedProgram === "1" || selectedProgram === "3") && activeTab === "risk-assessment" && (
              <RiskAssessment
                formData={formData}
                handleChange={handleChange}
                setActiveTab={setActiveTab}
                selectedProgram={selectedProgram}
                errors={errors}
              />
            )}

            {selectedProgram === "2" && activeTab === "aggression-history" && (
              <AggressionHistory
                formData={formData}
                handleChange={handleChange}
                setActiveTab={setActiveTab}
                selectedProgram={selectedProgram}
                errors={errors}
                onNext={handleAggressionHistoryNext}
              />
            )}

            {selectedProgram === "2" && activeTab === "client-behavior" && (
              <ClientBehavior
                formData={formData}
                handleChange={handleChange}
                setActiveTab={setActiveTab}
                selectedProgram={selectedProgram}
                errors={errors}
                onNext={handleClientBehaviorNext}
              />
            )}

            {selectedProgram === "2" && activeTab === "treatment-plan" && (
              <TreatmentPlan
                formData={formData}
                handleChange={handleChange}
                setActiveTab={setActiveTab}
                selectedProgram={selectedProgram}
                errors={errors}
              />
            )}
          </form>
        </div>
      </div>
    </div>
  );
};


export default AddPatientInquiry;